import Button from '@components/Button';
import Icon from '@components/Icon';
import useGetSrcChainExplorerUrl from '@hooks/useGetExplorerUrl';
import useWallets from '@hooks/useWallets';
import useRegisterToken from '@hooks/useRegisterToken';
import useTransferAssetFlowContext from '@hooks/useTransferAssetFlowContext';
import { getUrlWithAbsolutePathOrPublicUrl, shortenAddress } from '@lib/utils';
import { TxStatus, Network, TxChain, WalletType } from '@types';

interface Props {
  txnStatus: TxStatus;
  txnHash: string;
  network: Network;
  chain: TxChain | null;
  betaTx: boolean;
}

// TODO: manage any failed or cancelled state when design is ready
function TransferInfo({ txnStatus, txnHash, network, chain, betaTx }: Props) {
  const getExplorerUrl = useGetSrcChainExplorerUrl();
  const explorerUrl = chain ? getExplorerUrl(network, chain) : '';
  const { wallets } = useWallets();
  const [, secondWallet] = wallets;
  const { state } = useTransferAssetFlowContext();
  const { address, symbol, decimals, logoURI } = state.tokenAsset!;
  const tokenAddress =
    address[
      chain
        ? chain!
        : secondWallet?.type === WalletType.metamask
        ? secondWallet.chain
        : chain!
    ];
  const { registerToken, error } = useRegisterToken({
    tokenAddress,
    symbol,
    decimals,
    logoURI: getUrlWithAbsolutePathOrPublicUrl(logoURI),
  });

  const getTransactionId = () => {
    // TODO: remove below cronos-specific handling when explorer can support cosmos tx https://mcoproduct.atlassian.net/browse/CDP-272
    if (network === Network.Cronos) return '';
    return (
      <p className="text-smallRegular">
        Transaction ID:{' '}
        {txnHash ? (
          <a
            href={explorerUrl + txnHash}
            className="text-blue"
            target="_blank"
            rel="noreferrer"
          >
            {shortenAddress(txnHash)}
          </a>
        ) : (
          'fetching id...'
        )}
      </p>
    );
  };

  if (betaTx) {
    return (
      <div className="text-left">
        <p className="text-body">
          {symbol} transfer is on Beta. Check your destination wallet for your
          funds; it may take up to 1 hour.
        </p>
        {network === Network.Cronos &&
          symbol !== 'CRO' &&
          secondWallet?.type === WalletType.metamask && (
            <a
              href="#"
              className="text-blue items-center flex"
              onClick={(e) => {
                e.preventDefault();
                registerToken();
              }}
            >
              Add {symbol} to Metamask
              <Icon.Metamask className="ml-0.5" />
            </a>
          )}
        {error && <p className="mt-2 text-red">{error.message}</p>}
      </div>
    );
  }

  return (
    <div className="text-left">
      {(txnStatus === TxStatus.pending || txnStatus === TxStatus.failed) &&
        !txnHash && <p className="text-body">Waiting for Deposit</p>}
      {(txnStatus === TxStatus.pending || txnStatus === TxStatus.failed) &&
        txnHash && (
          <>
            <p className="text-body mb-0.5">Transfer Initiated</p>
            {getTransactionId()}
          </>
        )}
      {txnStatus === TxStatus.fulfilled && (
        <>
          <p className="text-body mb-0.5">Transfer Completed</p>
          {getTransactionId()}
        </>
      )}
      {network === Network.Cronos &&
        symbol !== 'CRO' &&
        secondWallet?.type === WalletType.metamask && (
          <a
            href="#"
            className="text-blue items-center flex"
            onClick={(e) => {
              e.preventDefault();
              registerToken();
            }}
          >
            Add {symbol} to Metamask
            <Icon.Metamask className="ml-0.5" />
          </a>
        )}
      {error && <p className="mt-2 text-red">{error.message}</p>}
    </div>
  );
}

export default TransferInfo;
